import { Stack, Typography } from '@mui/material';
import { Partner } from '@provision/entity-types';
import dayjs from 'dayjs';
import { BlockHeader, DynamicIcon, PartnerTimeZoneTooltip, Tooltip } from 'src/components';
import { useDisplayTime } from 'src/utils/hooks';
import { useCurrentDateTimer } from 'src/utils/hooks/use-current-date-timer';
import { useScreenDimensions } from 'src/utils/system';

const partnerTimeZoneTooltipProps = {
  placement: 'bottom-end' as const,
  slotProps: {
    popper: {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [-40, 0],
          },
        },
      ],
    },
  },
};

export const PartnerHeader = ({
  closeDetailsPanelTooltip,
  partner,
  onClose,
  displayPartnerInfo,
}: {
  closeDetailsPanelTooltip: string;
  partner?: Partial<Partner>;
  onClose?: () => void;
  displayPartnerInfo: boolean;
}) => {
  const now = useCurrentDateTimer();

  const currentDate = dayjs(now).tz(partner?.timeZone);

  const { displayTime } = useDisplayTime({
    showTimezone: true,
    tz: partner?.timeZone,
  });

  const { isMobile } = useScreenDimensions();

  if (!isMobile && !displayPartnerInfo) return null;

  return (
    <BlockHeader>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        padding={1}
        width="100%"
      >
        {isMobile && onClose ? (
          <Tooltip title={closeDetailsPanelTooltip}>
            <DynamicIcon
              name="right_panel_close"
              sx={{
                cursor: 'pointer',
                display: { sm: 'none' },
              }}
              onClick={onClose}
            />
          </Tooltip>
        ) : null}
        {displayPartnerInfo ? (
          <>
            <Typography
              variant="subtitle2"
              fontSize="0.75rem"
              sx={{
                textWrap: 'balance',
              }}
            >
              {partner?.businessName}
            </Typography>
            <PartnerTimeZoneTooltip
              date={new Date().toISOString()}
              partnerId={partner?.partnerId}
              tooltipProps={partnerTimeZoneTooltipProps}
            >
              <Typography
                variant="subtitle2"
                fontSize="0.75rem"
                textAlign="end"
                maxWidth="8rem"
                sx={{
                  textWrap: 'balance',
                }}
              >
                {currentDate.format('ddd, MMM D, YYYY')}
                <br />
                {displayTime(currentDate)}
              </Typography>
            </PartnerTimeZoneTooltip>
          </>
        ) : null}
      </Stack>
    </BlockHeader>
  );
};
